import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { SEOHead } from '../components/SEOHead';
import { Breadcrumbs } from '../components/seo/Breadcrumbs';
import { Card, CardContent } from '../components/ui/card';
import { ExternalLink, HelpCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FaqPage = () => {
  const [faq, setFaq] = useState([]);
  const [local, setLocal] = useState([]);

  useEffect(() => {
    axios.get(`${API}/seo/faq`).then((r) => setFaq(r.data)).catch(() => {});
    axios.get(`${API}/seo/local-pages?limit=12`).then((r) => setLocal(r.data)).catch(() => {});
  }, []);

  const jsonLd = {
    '@context': 'https://schema.org', '@type': 'FAQPage',
    mainEntity: faq.map((f) => ({ '@type': 'Question', name: f.question, acceptedAnswer: { '@type': 'Answer', text: f.answer } })),
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <SEOHead title="FAQ sous-traitance et réglementation du bâtiment en Belgique" description="Réponses factuelles et sourcées : trouver un sous-traitant fiable, assurances obligatoires (décennale, RC), agréation des entrepreneurs, TVA 6 %, Checkin@work, marchés publics, BCE." path="/faq" jsonLd={faq.length ? jsonLd : undefined} />
      <section className="bg-[#1E3A8A] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <Breadcrumbs items={[{ label: 'FAQ', path: '/faq' }]} />
          <h1 className="font-heading font-bold text-4xl sm:text-5xl mt-4 mb-3" data-testid="faq-title">Questions fréquentes – bâtiment et sous-traitance en Belgique</h1>
          <p className="text-blue-100 text-base md:text-lg max-w-2xl">Des réponses directes, chiffrées et sourcées (lois, SPF Economie, ONSS, SPF Finances) pour les entrepreneurs, sous-traitants et donneurs d'ordre.</p>
        </div>
      </section>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6">
        <nav className="bg-white border border-[#1E3A8A]/10 rounded-lg p-4 text-sm" data-testid="faq-toc">
          <p className="font-semibold mb-2">Sommaire</p>
          <ol className="list-decimal pl-5 space-y-1 text-[#374151]">
            {faq.map((f) => <li key={f.id}><a href={`#${f.id}`} className="hover:text-[#1E3A8A] hover:underline">{f.question}</a></li>)}
          </ol>
        </nav>
        {faq.map((f) => (
          <Card key={f.id} id={f.id} className="scroll-mt-24" data-testid={`faq-item-${f.id}`}>
            <CardContent className="p-6">
              <h2 className="font-heading font-bold text-lg md:text-xl text-[#111827] flex items-start gap-2"><HelpCircle className="w-5 h-5 text-[#F59E0B] mt-1 flex-shrink-0" /> {f.question}</h2>
              <p className="text-[#374151] mt-3 leading-relaxed">{f.answer}</p>
              <p className="text-xs text-[#6B7280] mt-3 flex flex-wrap gap-x-3 gap-y-1">
                <span className="font-semibold">Sources :</span>
                {f.sources.map((s) => <a key={s.url} href={s.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#1E3A8A] hover:underline"><ExternalLink className="w-3 h-3" />{s.name}</a>)}
              </p>
            </CardContent>
          </Card>
        ))}
        {local.length > 0 && (
          <div className="pt-4">
            <h2 className="font-heading font-bold text-lg mb-3">Trouver un sous-traitant près de chez vous</h2>
            <div className="flex flex-wrap gap-2" data-testid="faq-local-links">
              {local.map((l) => <Link key={l.path} to={l.path} className="text-sm bg-white border border-[#1E3A8A]/15 rounded-full px-3 py-1 hover:border-[#1E3A8A] text-[#1E3A8A]">{l.title}</Link>)}
            </div>
          </div>
        )}
        <p className="text-sm text-[#6B7280]">Voir aussi : <Link to="/blog" className="text-[#1E3A8A] underline">le blog</Link>, <Link to="/marches-publics" className="text-[#1E3A8A] underline">les chantiers publics</Link>, <Link to="/contractors" className="text-[#1E3A8A] underline">l'annuaire des sous-traitants</Link>.</p>
      </div>
    </div>
  );
};

export default FaqPage;
